import { useMemo, useState } from 'react';

interface TriviaQuestion {
  id: string;
  question: string;
  answer: string;
  explainer?: string;
}

interface Props {
  questions: TriviaQuestion[];
  triviaCurrentId?: string | null;
  controllerCount: number;
  onAdd: (question: string, answer: string, explainer: string) => void;
  onEdit: (id: string, question: string, answer: string, explainer: string) => void;
  onDelete: (id: string) => void;
}

export default function HostTriviaAdmin({
  questions, triviaCurrentId, controllerCount,
  onAdd, onEdit, onDelete,
}: Props) {
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQ, setEditQ] = useState('');
  const [editA, setEditA] = useState('');
  const [editE, setEditE] = useState('');
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [newE, setNewE] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return questions;
    return questions.filter(q =>
      q.question.toLowerCase().includes(f) || q.answer.toLowerCase().includes(f)
    );
  }, [questions, filter]);

  function startEdit(q: TriviaQuestion) {
    setEditingId(q.id);
    setEditQ(q.question);
    setEditA(q.answer);
    setEditE(q.explainer ?? '');
  }

  function commitEdit() {
    if (!editingId) return;
    if (!editQ.trim() || !editA.trim()) return;
    onEdit(editingId, editQ.trim(), editA.trim(), editE.trim());
    setEditingId(null);
  }

  function commitAdd() {
    if (!newQ.trim() || !newA.trim()) return;
    onAdd(newQ.trim(), newA.trim(), newE.trim());
    setNewQ('');
    setNewA('');
    setNewE('');
  }

  function commitDelete(id: string) {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(curr => curr === id ? null : curr), 3000);
      return;
    }
    setDeleteConfirmId(null);
    onDelete(id);
  }

  return (
    <section className="host-section">
      <div className="host-section-label">GAMY TRIVIA — QUESTION DATABASE ({questions.length})</div>

      <div className="host-trivia-toolbar">
        <a className="host-trivia-link" href="/party" target="_blank" rel="noreferrer">
          ▶ Open /party display
        </a>
        <span className="host-trivia-mode-badge">
          {controllerCount} {controllerCount === 1 ? 'controller' : 'controllers'} connected
        </span>
      </div>

      <input
        className="host-trivia-filter"
        type="text"
        placeholder={`Search ${questions.length} questions…`}
        value={filter}
        onChange={e => setFilter(e.target.value)}
      />

      <div className="host-trivia-list">
        {filtered.map(q => {
          const isEditing = editingId === q.id;
          const isCurrent = triviaCurrentId === q.id;
          return (
            <div key={q.id} className={`host-trivia-row ${isCurrent ? 'host-trivia-row-current' : ''}`}>
              {isEditing ? (
                <>
                  <textarea
                    className="host-trivia-input"
                    rows={2}
                    value={editQ}
                    onChange={e => setEditQ(e.target.value)}
                    placeholder="Question"
                  />
                  <textarea
                    className="host-trivia-input"
                    rows={2}
                    value={editA}
                    onChange={e => setEditA(e.target.value)}
                    placeholder="Answer"
                  />
                  <textarea
                    className="host-trivia-input"
                    rows={2}
                    value={editE}
                    onChange={e => setEditE(e.target.value)}
                    placeholder="Explainer (one-sentence context shown with the answer)"
                  />
                  <div className="host-trivia-row-actions">
                    <button className="host-correct-btn" onClick={commitEdit}>Save</button>
                    <button className="host-wrong-btn" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="host-trivia-q">
                    <span className="host-trivia-q-id">{q.id}</span>
                    {q.question}
                  </div>
                  <div className="host-trivia-a">→ {q.answer}</div>
                  {q.explainer && (
                    <div className="host-trivia-e">{q.explainer}</div>
                  )}
                  <div className="host-trivia-row-actions">
                    <button className="host-trivia-edit-btn" onClick={() => startEdit(q)}>Edit</button>
                    <button
                      className={`host-trivia-del-btn ${deleteConfirmId === q.id ? 'confirming' : ''}`}
                      onClick={() => commitDelete(q.id)}
                    >
                      {deleteConfirmId === q.id ? 'Confirm?' : 'Delete'}
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="host-trivia-empty">No questions match.</div>
        )}
      </div>

      <div className="host-trivia-add">
        <div className="host-trivia-add-label">+ Add Question</div>
        <textarea
          className="host-trivia-input"
          rows={2}
          placeholder="Question"
          value={newQ}
          onChange={e => setNewQ(e.target.value)}
        />
        <textarea
          className="host-trivia-input"
          rows={2}
          placeholder="Answer"
          value={newA}
          onChange={e => setNewA(e.target.value)}
        />
        <textarea
          className="host-trivia-input"
          rows={2}
          placeholder="Explainer (optional, one-sentence context shown with the answer)"
          value={newE}
          onChange={e => setNewE(e.target.value)}
        />
        <button
          className="host-fj-reveal-btn"
          onClick={commitAdd}
          disabled={!newQ.trim() || !newA.trim()}
        >
          Add to Database
        </button>
      </div>
    </section>
  );
}
